import React, { useState, useRef, useEffect, useCallback } from "react"
import { Button, Modal } from "antd"
import logoIter from "assets/images/logo_iter.jpg"
import { PrinterOutlined } from "@ant-design/icons"
import { getBase64, objetoVacio } from "util/utils"
import LoaderComponent from "components/LoaderTerpel/LoaderTerpel"
import ".././estilos/ticketcss/IterReceipt.css"
import { ISellRecaudoTicket } from "./sellTickets/recaudos/interface"
import { SELL_TYPES_TICKETS } from "./sellTickets/constants"
import SellRecaudoTicket from "./sellTickets/recaudos/SellRecaudoTicket"
import { ISellCreditTicket } from "../../ReporteVenta/components/sellTickets/utils/interfaceTickets"
import MainCreditTicketStruct from "../../ReporteVenta/components/sellTickets/credit/MainCreditTicketStruct"


interface IterReceiptProps {
  tickets: Array<ISellRecaudoTicket | ISellCreditTicket>;

  getTickes: () => void;
  isLoading: boolean;
  disabled: boolean;

  ticketLimit: boolean;
  numberOfTicketIds: number;
}

export default function SalesReportReceipt({ tickets, getTickes, isLoading, disabled, ticketLimit, numberOfTicketIds }: IterReceiptProps) {
  const [isPreviewVisible, setIsPreviewVisible] = useState(false)
  const [isConfirmVisible, setIsConfirmVisible] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false)
  const receiptRefs = useRef<HTMLDivElement[]>([])
  const [isPrinting, setIsPrinting] = useState(false)
  const receiptRef = useRef<HTMLDivElement>(null)
  const [logoBase, setLogoBase] = useState("")
  const imageRef = useRef<HTMLImageElement>(null)

  useEffect(() => {
    const loadImage = async () => {
      try {
        const base64 = await getBase64(logoIter)
        setLogoBase(base64)
        setIsImageLoaded(true)
      } catch (error) {
        console.error("Error loading image:", error)
        setLogoBase("/placeholder.svg")
        setIsImageLoaded(true)
      }
    }
    loadImage()
  }, [])

  const handlePreview = () => {
    if (ticketLimit) {
      setIsConfirmVisible(true); // Muestra el modal de confirmación si hay más de 10 tickets
    } else {
      setIsPreviewVisible(true); // Si hay 10 o menos, muestra la vista previa directamente
      getTickes();
    }
  };

  const handleConfirmOk = () => {
    setIsConfirmVisible(false);
    setIsPreviewVisible(true); // Muestra la vista previa si el usuario acepta
    getTickes();
  };

  const handleConfirmCancel = () => {
    setIsConfirmVisible(false); // Cierra el modal si el usuario cancela
  };


  const handlePrint = useCallback(() => {
    if (!isImageLoaded) {
      console.error("Image not loaded yet")
      return
    }

    setIsPrinting(true)

    // Create an iframe to handle silent printing
    const iframe = document.createElement("iframe")
    iframe.style.display = "none"
    document.body.appendChild(iframe)

    // Get the receipt content
    const receiptsHtml = tickets
      .map((_, index) => {
        const receiptHtml = receiptRefs.current[index]?.outerHTML || "";
        return receiptHtml.replace(`src="${logoIter}"`, `src="${logoBase}"`);
      })
      .join('<div class="receipt__separator"></div>');

    // Write the content to the iframe
    const iframeDoc = iframe.contentWindow?.document

    const styles = `
    <style>
      @media print {
        /* General Styles */
        body {
        margin: 0;
        padding: 0;
        font-family: "Consolas", "Liberation Mono", "Menlo", monospace;
        font-weight: 400;
        font-stretch: condensed;
        -webkit-font-stretch: condensed;
        font-variant-numeric: tabular-nums;

        }
        .receipt {
          width: 80mm;
          page-break-before: always;
          background-color: white;
          font-family: "Consolas", "Liberation Mono", "Menlo", monospace;
          font-size: 12px;
          padding-bottom: 2rem;
          margin-bottom: 2rem;
          letter-spacing: 0px;
          font-weight: 400;
          font-stretch: condensed;
          -webkit-font-stretch: condensed;
          font-variant-numeric: tabular-nums;
        }

        .receipt:last-child {
          border-bottom: none;
          margin-bottom: 0;
          padding-bottom: 0;
        }

        .receipt p {
          margin: 0;
          padding: 0;
          line-height: 2;
          /* Ajusta el espaciado entre líneas según sea necesario */
        }

        .receipt__preview-container {
          padding: 1.8rem;
          background-color: #f0f0f0;
          border-radius: 8px;
        }

        .receipt table {
          width: 100%;
        }

        .receipt__text--center {
          text-align: center;
          width: 100%;
        }

        /* Separators */
        .receipt__separator {
          border-top: 1px dashed #000;
          height: 1px;
        }

        .receipt__table td,
        .receipt__table th {
          font-size: 12px;
          font-weight: 400;
          font-stretch: condensed;
        }

        .receipt__table-cell,
        .receipt__table-header {
          text-align: left;
          vertical-align: middle;
        }

        /* .receipt__table-cell {
          padding: 1mm 0;
        } */

        .mt-2 {
          margin-top: 20px
        }

        .text-center {
          text-align: center !important;
        }
        @page {
          margin: 0;
          size: 80mm 297mm;
        }
      }
    </style>
  `;
    if (iframeDoc) {
      iframeDoc.write(`
        <!DOCTYPE html>
        <html>
          <head>
            ${styles}
          </head>
          <body>
            ${receiptsHtml.replace(`src="${logoIter}"`, `src="${logoBase}"`)}
          </body>
        </html>
      `)
      iframeDoc.close()

      // Wait for images to load
      try {
        // Access the iframe's window object
        const iframeWindow = iframe.contentWindow
        if (iframeWindow) {
          // Set up beforeprint and afterprint listeners
          iframeWindow.addEventListener("beforeprint", () => {
            setIsPrinting(true)
          })

          iframeWindow.addEventListener("afterprint", () => {
            setIsPrinting(false)
            setIsPreviewVisible(false)
            document.body.removeChild(iframe)
          })

          // Print without showing dialog
          iframeWindow.focus()
          iframeWindow.print()
        }
      } catch (error) {
        console.error("Print error:", error)
        document.body.removeChild(iframe)
        setIsPrinting(false)
      }
    }
  }, [isImageLoaded, logoBase, tickets])

  useEffect(() => {
    if (imageRef.current && logoBase) {
      imageRef.current.src = logoBase
    }
  }, [logoBase])


  const renderTickets = (ticket: ISellRecaudoTicket | ISellCreditTicket, index: number) => {

    const ticketsCredito = ticket as ISellCreditTicket
    const ticketsRecaudos = ticket as ISellRecaudoTicket

    return (
      <div
        key={index}
        ref={(el) => (receiptRefs.current[index] = el as HTMLDivElement)}
        className="receipt"
      >
        {ticket.tipo_venta === SELL_TYPES_TICKETS.CLIENTE_CREDITO ? (
          <MainCreditTicketStruct data={ticketsCredito} imageRef={imageRef}  logoBase={logoBase}/>
        ) : (
          <SellRecaudoTicket
            imageRef={imageRef}
            logoBase={logoBase}
            ticket={ticketsRecaudos}
            setLogoBase={setLogoBase}
            setIsImageLoaded={setIsImageLoaded}
          />
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center gap-2 m-auto">
      <Button disabled={disabled} icon={<PrinterOutlined style={{ height: '5rem' }} />} onClick={handlePreview} className="min-w-[80mm]">
        Generar Ticket(s)
      </Button>
      {/* Modal de Confirmación */}
      {isConfirmVisible && < Modal
        visible={isConfirmVisible}
        onOk={handleConfirmOk}
        onCancel={handleConfirmCancel}
        title="Confirmación"
        okText="Aceptar"
        cancelText="Cancelar"
      >
        <p>Se han seleccionado más de 10 registros. <strong>({numberOfTicketIds})</strong> ¿Deseas imprimirlos?</p>
      </Modal>}

      {
        (isPreviewVisible) && (
          <Modal
            visible={isPreviewVisible}
            onCancel={() => !isPrinting && setIsPreviewVisible(false)}
            footer={null}
            title="Vista Previa"
            width={450}
            className="overflow-auto"
            maskClosable={!isPrinting}
            closable={!isPrinting}
          >
            <div className="flex flex-col p-2 m-2 gap-2">
              <div
                className="receipt__preview-container"
                style={{
                  minHeight: "400px",
                  maxHeight: "65vh",
                  overflowY: "auto",
                }}
              >
                {isLoading ? <LoaderComponent text="generando tickets..." /> : tickets.map((ticket, index) => renderTickets(ticket, index))}
              </div>
            </div>
            <div className="flex flex-col items-center justify-center">

              <Button onClick={handlePrint} className="mx-auto" disabled={isLoading || isPrinting} loading={isPrinting}>
                {isPrinting ? "Imprimiendo..." : "Imprimir"}
              </Button>
            </div>
          </Modal>
        )
      }
    </div >
  )
}
